import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline } from "@/components/primitives";
import { StewardStoneNav } from "@/components/StewardStoneNav";

const SECTIONS = [
  { t: "Accountability & Decision Authority", d: "Mapping who owns which decisions, where authority is unclear, and where accountability gaps slow or distort execution." },
  { t: "Resource Allocation", d: "Examining how people, budget, and time are assigned against actual demand rather than historical habit." },
  { t: "Communication Structures", d: "Locating where handoffs, escalations, and information flow fail between roles, shifts, or departments." },
  { t: "Operational Bottlenecks", d: "Isolating the constraints that cap throughput and create backlog, rework, or delay." },
  { t: "Scalability & Continuity", d: "Testing whether current processes hold up under growth, turnover, or disruption — and where they break first." },
  { t: "Measurement", d: "Defining the few metrics that actually indicate whether the operation is improving, and how they are tracked." },
];

export default function OperationalStrategy() {
  return (
    <div className="glow-navy min-h-screen">
      <Seo title="Operational Strategy — Steward & Stone" description="Steward & Stone Operational Strategy — organizational and operational assessment, workflow and gap analysis, systems redesign, and practical implementation planning. Broader than auditing." />
      <StewardStoneNav />

      <section className="pt-12 md:pt-16 pb-8 px-6 text-center">
        <Reveal>
          <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Steward &amp; Stone</p>
          <h1 className="mt-3 font-serif2 text-4xl md:text-5xl text-[var(--bone)] leading-tight">Operational Strategy</h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed">
            We assess organizations, operations, and systems — identify what is not working and why — and design practical strategies for improvement. This is broader than auditing.
          </p>
        </Reveal>
      </section>

      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal><Hairline className="opacity-70 mb-12" /></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="corner-frame px-7 py-8 h-full">
                  <h3 className="font-serif2 text-xl md:text-2xl text-[var(--bone)]">{s.t}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <p className="text-[var(--bone)]/80 leading-relaxed">
            The work concentrates on the mechanics that determine whether an organization runs well: workflow, accountability, resource allocation, decision authority, communication failures, operational bottlenecks, scalability, continuity, and how results are measured.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8"><PillButton to="/steward-and-stone/assessment" testid="operational-inquiry">Request an Assessment</PillButton></Reveal>
      </section>
    </div>
  );
}
